'use client';

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

type Politeness = 'polite' | 'assertive';

interface LiveRegionContextType {
  /**
   * Announce a message to screen readers
   * @param message - The message to announce
   * @param politeness - 'polite' (default) waits for user to be idle, 'assertive' interrupts immediately
   */
  announce: (message: string, politeness?: Politeness) => void;
}

const LiveRegionContext = createContext<LiveRegionContextType | null>(null);

interface LiveRegionProviderProps {
  children: ReactNode;
}

/**
 * Provider for screen reader announcements.
 * Use the `useAnnounce` hook to trigger announcements.
 *
 * @example
 * ```tsx
 * // In a component
 * const announce = useAnnounce();
 *
 * // Announce politely (waits for idle)
 * announce('Item added to cart');
 *
 * // Announce assertively (interrupts)
 * announce('Error: Payment failed', 'assertive');
 * ```
 */
export function LiveRegionProvider({ children }: LiveRegionProviderProps) {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');

  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    const setter = politeness === 'polite' ? setPoliteMessage : setAssertiveMessage;

    // Clear first to ensure the same message triggers a new announcement
    setter('');

    // Use setTimeout to ensure the DOM update triggers the announcement
    setTimeout(() => {
      setter(message);
    }, 100);

    // Clear after a delay to allow the message to be read
    setTimeout(() => {
      setter('');
    }, 5000);
  }, []);

  return (
    <LiveRegionContext.Provider value={{ announce }}>
      {children}
      {/* Polite announcements - waits for user to be idle */}
      <div aria-live="polite" aria-atomic="true" className="sr-only" role="status">
        {politeMessage}
      </div>
      {/* Assertive announcements - interrupts immediately */}
      <div aria-live="assertive" aria-atomic="true" className="sr-only" role="alert">
        {assertiveMessage}
      </div>
    </LiveRegionContext.Provider>
  );
}

/**
 * Hook to access the announcement function.
 * Must be used within a LiveRegionProvider.
 */
export function useAnnounce(): (message: string, politeness?: Politeness) => void {
  const context = useContext(LiveRegionContext);

  if (!context) {
    throw new Error('useAnnounce must be used within a LiveRegionProvider');
  }

  return context.announce;
}
